import { useState, useCallback, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import type { UserRole } from './types/gateway';
import type { CorrectionFormData } from './components/training';
import { useGateway } from './hooks/useGateway';
import { useSessions } from './hooks/useSessions';
import { useMessages } from './hooks/useMessages';
import { useKnowledge } from './hooks/useKnowledge';
import { useThreads } from './hooks/useThreads';
import { TopBar } from './components/TopBar';
import { Sidebar } from './components/Sidebar';
import { ChatArea } from './components/ChatArea';
import { InputBar } from './components/InputBar';
import { QuickActions } from './components/QuickActions';
import TrainingPage from './pages/TrainingPage';
import styles from './App.module.css';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isMobile;
}

export default function App() {
  const [role, setRole] = useState<UserRole>('designer');
  const [sidebarTab, setSidebarTab] = useState<'threads' | 'knowledge'>('threads');

  // Threads (Issue 3)
  const { threads, activeThreadId, setActiveThreadId } = useThreads(role);

  // Mobile sidebar toggle (Issue 4)
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  // Keep sidebarOpen in sync when switching between desktop / mobile
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  // Auto-close sidebar when a thread is selected on mobile
  const handleSelectThread = useCallback(
    (id: string) => {
      setActiveThreadId(id);
      if (isMobile) {
        setSidebarOpen(false);
      }
    },
    [isMobile, setActiveThreadId]
  );

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  // Gateway connection
  const { status, send, onMessage } = useGateway(role, `user-${role}`);

  // Session management
  const { sessions, activeSessionId, createSession, switchSession } = useSessions({ send, onMessage });

  // Knowledge / learning
  const { entries: knowledgeEntries, searchKnowledge, teachMode, toggleTeachMode, submitCorrection } = useKnowledge({ send, activeSessionId });

  // Messages (with teach mode support)
  const { messages, isStreaming, sendMessage } = useMessages({
    send,
    onMessage,
    activeSessionId,
    teachMode,
  });

  const handleSend = useCallback(
    (content: string, files?: File[]) => {
      sendMessage(content, files);
    },
    [sendMessage]
  );

  const handleQuickAction = useCallback(
    (prompt: string) => {
      sendMessage(prompt);
    },
    [sendMessage]
  );

  const handleCorrect = useCallback(
    (messageId: string, data: CorrectionFormData) => {
      submitCorrection(messageId, data.correctedValue, data.category);
    },
    [submitCorrection]
  );

  // Issue 2: wire onAction — send the action string as a message
  const handleAction = useCallback(
    (action: string) => {
      sendMessage(action);
    },
    [sendMessage]
  );

  const chatView = (
    <>
      <div className={styles.main}>
        <Sidebar
          threads={threads}
          activeThreadId={activeThreadId}
          onSelectThread={handleSelectThread}
          currentRole={role}
          sidebarTab={sidebarTab}
          onTabChange={setSidebarTab}
          knowledgeEntries={knowledgeEntries}
          onSearchKnowledge={searchKnowledge}
          sidebarOpen={sidebarOpen}
        />
        <div className={styles.chat}>
          <ChatArea
            messages={messages}
            isStreaming={isStreaming}
            onCorrect={handleCorrect}
            onAction={handleAction}
          />
          <QuickActions role={role} onSelect={handleQuickAction} />
          <InputBar
            onSend={handleSend}
            teachMode={teachMode}
            onToggleTeachMode={toggleTeachMode}
            disabled={status !== 'connected'}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className={styles.app}>
      <TopBar
        connectionStatus={status}
        currentRole={role}
        onRoleChange={setRole}
        sessions={sessions}
        activeSessionId={activeSessionId}
        onCreateSession={createSession}
        onSwitchSession={switchSession}
        onToggleSidebar={toggleSidebar}
      />
      <Routes>
        <Route path="/" element={chatView} />
        <Route path="/training" element={<TrainingPage />} />
      </Routes>
    </div>
  );
}
